// Página Processos: leitura dos e-mails dos tribunais (Gmail) que atualiza os processos citados
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Mail, MailCheck } from 'lucide-react';
import {
  GoogleCalendarStatus,
  connectGoogleCalendar,
  getGoogleCalendarStatus,
  syncEmailsNow,
} from '@/services/googleCalendarSync';

const formatTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : 'ainda não';

export const EmailCaseSyncButton = ({ onSynced, iconOnly = false }: { onSynced?: () => void; iconOnly?: boolean }) => {
  const { toast } = useToast();
  const [status, setStatus] = useState<GoogleCalendarStatus | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => setStatus(await getGoogleCalendarStatus().catch(() => null));

  useEffect(() => {
    refresh();
  }, []);

  // Fora do ar (ambiente local) ou sem sessão: não mostra
  if (!status) return null;

  const result = status.gmailLastResult;

  const handleRead = async () => {
    setBusy(true);
    try {
      const r = await syncEmailsNow();
      if (r.erro) throw new Error(r.erro);
      const novos = r.atualizados.reduce((acc, a) => acc + a.novos, 0);
      toast({
        title: 'E-mails lidos',
        description: r.atualizados.length
          ? `${r.atualizados.length} processo(s) atualizado(s), ${novos} andamento(s) novo(s).`
          : `${r.emails} e-mail(s) de hoje, nenhum de processo cadastrado.`,
      });
      onSynced?.();
    } catch (error: any) {
      toast({ title: 'Erro ao ler e-mails', description: error.message, variant: 'destructive' });
    } finally {
      setBusy(false);
      refresh();
    }
  };

  const handleAuthorize = async () => {
    setBusy(true);
    try {
      await connectGoogleCalendar();
    } catch (error: any) {
      setBusy(false);
      toast({ title: 'Erro', description: error.message, variant: 'destructive' });
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        {iconOnly ? (
          <button type="button" className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-[#faf7f2] hover:text-[#7a6440] relative" title="E-mails dos tribunais" aria-label="E-mails dos tribunais">
            {status.gmailEnabled ? <MailCheck className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
            {status.gmailEnabled && !result?.erro && <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-emerald-500" />}
            {result?.erro && <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-amber-500" />}
          </button>
        ) : (
          <Button variant="ghost" className="text-slate-500 hover:text-slate-900">
            {status.gmailEnabled ? <MailCheck className="mr-2 h-4 w-4 text-emerald-600" /> : <Mail className="mr-2 h-4 w-4" />}
            E-mails dos tribunais
            {result?.erro && <span className="ml-1.5 h-2 w-2 rounded-full bg-amber-500" title="Última leitura com erro" />}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-3 rounded-2xl border-[#c4ab83]/30 p-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Atualização automática</p>
          <p className="text-sm font-semibold text-slate-900">E-mails dos tribunais</p>
          {status.email && <p className="text-xs text-slate-500">{status.email}</p>}
        </div>

        {!status.connected || !status.gmailEnabled ? (
          <>
            <p className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
              Autorize a leitura dos e-mails para o sistema atualizar os processos citados nos avisos dos tribunais, todo dia às 20h.
            </p>
            <Button size="sm" onClick={handleAuthorize} disabled={busy} className="h-9 w-full rounded-lg bg-[#c4ab83] text-xs font-semibold text-slate-900 hover:bg-[#b39a72]">
              {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Mail className="mr-1.5 h-3.5 w-3.5" />}
              Autorizar leitura dos e-mails
            </Button>
          </>
        ) : (
          <>
            <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
              <p>Todo dia às 20h o sistema lê os e-mails do dia, registra o aviso como andamento e atualiza o processo pelo Datajud.</p>
              <p className="mt-1.5 text-slate-400">Última leitura: {formatTime(status.gmailLastSync)}</p>
              {result && !result.erro && (
                <p className="mt-0.5 text-slate-400">
                  {result.emails} e-mail(s) · {result.atualizados.length} processo(s) atualizado(s)
                </p>
              )}
              {result?.erro && <p className="mt-1 text-amber-700">Atenção: {result.erro}</p>}
            </div>

            {result && result.atualizados.length > 0 && (
              <ul className="space-y-1 text-xs">
                {result.atualizados.slice(0, 5).map((a) => (
                  <li key={a.numero} className="flex items-center justify-between gap-2 rounded-lg px-2 py-1 hover:bg-[#faf7f2]">
                    <span className="truncate text-slate-700">{a.titulo}</span>
                    <span className="shrink-0 tabular-nums text-slate-400">+{a.novos}</span>
                  </li>
                ))}
              </ul>
            )}

            {result && result.naoCadastrados.length > 0 && (
              <p className="rounded-lg bg-amber-50 p-2 text-xs text-amber-800">
                Citados nos e-mails e ainda não cadastrados: {result.naoCadastrados.join(', ')}
              </p>
            )}

            <Button size="sm" onClick={handleRead} disabled={busy} className="h-9 w-full rounded-lg bg-slate-900 text-xs text-white hover:bg-slate-800">
              {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Mail className="mr-1.5 h-3.5 w-3.5 text-[#e8d5a8]" />}
              Ler e-mails agora
            </Button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};
